/**
 * Case notes, tags and bookmarks.
 *
 * An analyst's working material rather than evidence: nothing here is ever cited, and
 * nothing here reaches a report. That separation is deliberate — a note is where somebody
 * writes a hunch, and a hunch that could end up in a document about a named person is the
 * failure the whole citation design exists to prevent.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { Bookmark, Note, NoteRequest, Tag, TagRequest } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

/** The workspace panel for one case. */
export function NotesPanel({ caseId }: { caseId: string }): ReactElement {
  const canEdit = useCan('create_run_cases');
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const [label, setLabel] = useState('');

  const notes = useQuery({
    queryKey: ['notes', caseId],
    queryFn: () => request<Note[]>(`/cases/${caseId}/notes`),
  });
  const tags = useQuery({
    queryKey: ['tags', caseId],
    queryFn: () => request<Tag[]>(`/cases/${caseId}/tags`),
  });
  const bookmarks = useQuery({
    queryKey: ['bookmarks', caseId],
    queryFn: () => request<Bookmark[]>(`/cases/${caseId}/bookmarks`),
  });

  const addNote = useMutation({
    mutationFn: () => request<Note, NoteRequest>(`/cases/${caseId}/notes`, { method: 'POST', body: { body } }),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['notes', caseId] });
    },
  });
  const deleteNote = useMutation({
    mutationFn: (noteId: string) => request<void>(`/notes/${noteId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notes', caseId] }),
  });
  const addTag = useMutation({
    mutationFn: () => request<Tag, TagRequest>(`/cases/${caseId}/tags`, { method: 'POST', body: { label } }),
    onSuccess: () => {
      setLabel('');
      void queryClient.invalidateQueries({ queryKey: ['tags', caseId] });
    },
  });
  const removeTag = useMutation({
    mutationFn: (tagId: string) => request<void>(`/tags/${tagId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tags', caseId] }),
  });
  const removeBookmark = useMutation({
    mutationFn: (bookmarkId: string) =>
      request<void>(`/bookmarks/${bookmarkId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['bookmarks', caseId] }),
  });

  if (notes.isLoading || tags.isLoading) return <Loading />;
  if (notes.error) return <ErrorState error={notes.error} />;

  return (
    <div className="space-y-4">
      <Card title="Tags">
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(tags.data ?? []).map((tag) => (
              <span key={tag.id} className="inline-flex items-center gap-1">
                <Badge tone="accent">{tag.label}</Badge>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeTag.mutate(tag.id)}
                    aria-label={`Remove tag ${tag.label}`}
                    className="text-xs text-muted hover:text-danger"
                  >
                    ✕
                  </button>
                )}
              </span>
            ))}
            {(tags.data ?? []).length === 0 && (
              <span className="text-sm text-muted">No tags.</span>
            )}
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <input
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Add a tag"
                maxLength={48}
                className="rounded border border-line bg-surface px-2 py-1 text-sm"
              />
              <Button disabled={!label.trim() || addTag.isPending} onClick={() => addTag.mutate()}>
                Add
              </Button>
            </div>
          )}
          {addTag.error != null && <ErrorState error={addTag.error} />}
        </div>
      </Card>

      <Card title="Notes">
        <div className="space-y-3">
          <p className="text-xs text-muted">
            Working notes. Nothing here is cited and nothing here reaches a report — a note
            is where a hunch goes, and a hunch must not end up in a document about a named
            person.
          </p>
          {canEdit && (
            <div className="space-y-2">
              <textarea
                rows={3}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder="What did you notice?"
                className="w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
              />
              <Button
                tone="primary"
                disabled={!body.trim() || addNote.isPending}
                onClick={() => addNote.mutate()}
              >
                Add note
              </Button>
            </div>
          )}
          <ul className="space-y-2">
            {(notes.data ?? []).map((note) => (
              <li key={note.id} className="rounded border border-line bg-surface p-3 text-sm">
                <p className="whitespace-pre-wrap">{note.body}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-muted">
                  <span>{note.created_at.slice(0, 19).replace('T', ' ')}</span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => deleteNote.mutate(note.id)}
                      className="hover:text-danger"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
            {(notes.data ?? []).length === 0 && (
              <li className="text-sm text-muted">No notes yet.</li>
            )}
          </ul>
        </div>
      </Card>

      <Card title="Bookmarks">
        <ul className="space-y-1 text-sm">
          {(bookmarks.data ?? []).map((bookmark) => (
            <li key={bookmark.id} className="flex items-center justify-between">
              <span>
                <Badge>{bookmark.target_kind}</Badge>{' '}
                <span className="font-mono text-xs">{bookmark.target_id.slice(0, 8)}</span>{' '}
                {bookmark.note}
              </span>
              <button
                type="button"
                onClick={() => removeBookmark.mutate(bookmark.id)}
                className="text-xs text-muted hover:text-danger"
              >
                Remove
              </button>
            </li>
          ))}
          {(bookmarks.data ?? []).length === 0 && (
            <li className="text-muted">Nothing bookmarked.</li>
          )}
        </ul>
      </Card>
    </div>
  );
}
